import { useCallback, useEffect, useState } from 'react';

import { maintenanceAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

/**
 * Historique atelier d'un camion: ordres, pannes, relevés, plans,
 * immobilisations. Les papiers ne sont plus chargés ici: ils ont leur onglet.
 * Chaque liste est lue en entier; ce sont les écrans qui la présentent par pages.
 *
 * `enabled` à faux (module maintenance absent de la licence) ne lance aucun
 * appel: une seule réponse 403 dans le `Promise.all` faisait tomber toute la
 * fiche en erreur.
 */
export function useTruckMaintenance(truckId, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(false);

  const load = useCallback(async ({ silencieux = false } = {}) => {
    if (!enabled || !truckId) {
      setLoading(false);
      return;
    }
    if (!silencieux) setLoading(true);
    setError(false);
    // Tout l'historique du camion, page après page: les totaux (dépense atelier,
    // dernier compteur) portent sur toutes les lignes, jamais sur les premières.
    const tout = (lire) => fetchAllPages(({ page, page_size: taille }) => lire({ truck_id: truckId, page, page_size: taille }));
    try {
      const [orders, breakdowns, readings, plans, immobilizations] = await Promise.all([
        tout((params) => maintenanceAPI.getWorkOrdersPage(params)),
        tout((params) => maintenanceAPI.getBreakdownsPage(params)),
        tout((params) => maintenanceAPI.getReadingsPage(params)),
        maintenanceAPI.getPlans({ truck_id: truckId }),
        tout((params) => maintenanceAPI.getImmobilizationsPage(params)),
      ]);
      setData({
        orders: orders.items,
        breakdowns: breakdowns.items,
        readings: readings.items,
        plans: Array.isArray(plans) ? plans : [],
        immobilizations: immobilizations.items,
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [truckId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  // Un ordre de travail, une panne ou une immobilisation saisis ailleurs.
  useRafraichirSur(['maintenance', 'camions'], load, { actif: Boolean(enabled && truckId) });

  return { data, loading, error, reload: load };
}

export default useTruckMaintenance;
